import type { Oauth2SegmentAuthResponse } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import { getApiClientErrorBody, getApiClientErrorStatus } from '@/services/api/errors';
import { openSegmentOAuthPopup } from '@/services/oauth';
import { SegmentAuthAction, SegmentAuthResult, mapActionToResult } from './actions';

export { SegmentAuthAction, SegmentAuthResult, mapActionToResult } from './actions';

/**
 * Segment source connect, verify and disconnect
 * (`POST /v1/environments/{id}/oauth2/segment`).
 *
 * Every action authorizes through the Segment OAuth popup first: the backend needs
 * a fresh authorization code to talk to the reader's Segment workspace, even to
 * disable or delete. The action then says what to do with the Customer.io
 * destination once the code is exchanged.
 */

/**
 * What the mismatch errors attach, so the screen can name the destination it found.
 *
 * `destination mismatch` carries the trio; `environment_id` is null when no
 * workspace owns the site id. `source mismatch` carries the two source names
 * instead, which is why every key is optional.
 */
export type SegmentAuthErrorMeta = {
  site_id?: string;
  environment_id?: number | null;
  source?: string;
  existing_source_name?: string;
  new_source_name?: string;
};

/** One error from the API's `errors` array. */
export type SegmentAuthApiError = {
  detail?: string;
  status?: string;
  meta?: SegmentAuthErrorMeta;
};

/**
 * The outcome of an attempt.
 *
 * `enabled` is only present when the API answered with a body, which happens for
 * `Check`, and for `Create` when the destination already existed.
 */
export type SegmentAuthOutcome = {
  state: SegmentAuthResult;
  action: SegmentAuthAction;
  enabled?: boolean;
  errors?: SegmentAuthApiError[];
};

/**
 * Why an attempt failed, for the screen to choose its copy.
 *
 * `DestinationExists` is separated because it is not really a failure: the reader
 * picked a Segment workspace that already has a Customer.io destination.
 */
export const SegmentAuthFailure = {
  /** The popup was closed, or the reader is no longer authorized with Segment. */
  Authorization: 'authorization',
  /** The chosen Segment workspace already has a Customer.io destination. */
  DestinationExists: 'destination-exists',
  /** The destination is gone, or belongs to a different source or workspace. */
  DestinationMismatch: 'destination-mismatch',
  Unknown: 'unknown',
} as const;

export type SegmentAuthFailure = (typeof SegmentAuthFailure)[keyof typeof SegmentAuthFailure];

export class SegmentAuthError extends Error {
  readonly failure: SegmentAuthFailure;
  readonly action: SegmentAuthAction;
  readonly errors: SegmentAuthApiError[];

  constructor(
    failure: SegmentAuthFailure,
    action: SegmentAuthAction,
    errors: SegmentAuthApiError[] = [],
    options?: ErrorOptions,
  ) {
    super(`Segment authentication failed: ${failure}`, options);
    this.name = 'SegmentAuthError';
    this.failure = failure;
    this.action = action;
    this.errors = errors;
  }
}

/** Details the mismatch branches key on, kept as their own list so the copy can name them. */
const MISMATCH_DETAILS = ['destination not found', 'source mismatch', 'destination mismatch'];
const DESTINATION_EXISTS_DETAIL = 'destination already exists';

function asString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function asMeta(value: unknown): SegmentAuthErrorMeta | undefined {
  return value != null && typeof value === 'object' ? value : undefined;
}

/** Narrows one entry of the API's `errors` array; anything unexpected drops out. */
function toApiError(raw: unknown): SegmentAuthApiError {
  if (raw == null || typeof raw !== 'object') return {};

  return {
    detail: 'detail' in raw ? asString(raw.detail) : undefined,
    status: 'status' in raw ? asString(raw.status) : undefined,
    meta: 'meta' in raw ? asMeta(raw.meta) : undefined,
  };
}

function readApiErrors(error: unknown): SegmentAuthApiError[] {
  const body = getApiClientErrorBody(error);
  if (body == null || typeof body !== 'object' || !('errors' in body)) return [];

  const { errors } = body;
  return Array.isArray(errors) ? errors.map((raw: unknown) => toApiError(raw)) : [];
}

function classifyFailure(errors: SegmentAuthApiError[], status: number | undefined): SegmentAuthFailure {
  if (errors.some((apiError) => apiError.detail === DESTINATION_EXISTS_DETAIL)) {
    return SegmentAuthFailure.DestinationExists;
  }

  if (errors.some((apiError) => apiError.detail != null && MISMATCH_DETAILS.includes(apiError.detail))) {
    return SegmentAuthFailure.DestinationMismatch;
  }

  // The API reports a missing destination as a 404 without a matching detail, and
  // the first error carries the status as a string.
  if (errors[0]?.status === '404' || status === 404) return SegmentAuthFailure.DestinationMismatch;

  return SegmentAuthFailure.Unknown;
}

export type AuthenticateSegmentInput = {
  workspaceId: number;
  /** From the workspace's API credentials. */
  siteId: string;
  apiKey: string;
  action?: SegmentAuthAction;
};

/**
 * Authorizes with Segment, then applies the action to the destination.
 *
 * A closed popup and a revoked authorization both surface as
 * {@link SegmentAuthFailure.Authorization}: Segment reports neither distinctly, and
 * the reader's next step is the same either way.
 */
export async function authenticateSegment({
  workspaceId,
  siteId,
  apiKey,
  action = SegmentAuthAction.Default,
}: AuthenticateSegmentInput): Promise<SegmentAuthOutcome> {
  let authorization;
  try {
    authorization = await openSegmentOAuthPopup();
  } catch (error) {
    throw new SegmentAuthError(SegmentAuthFailure.Authorization, action, [], { cause: error });
  }

  let response: Oauth2SegmentAuthResponse | undefined;
  try {
    response = await hydraUiApi.oauth2.segment({
      params: { path: { environment_id: String(workspaceId) } },
      body: {
        code: authorization.authorizationCode,
        redirect_uri: authorization.redirectUri,
        site_id: siteId,
        api_key: apiKey,
        action,
      },
    });
  } catch (error) {
    const errors = readApiErrors(error);
    throw new SegmentAuthError(classifyFailure(errors, getApiClientErrorStatus(error)), action, errors, {
      cause: error,
    });
  }

  // The create, enable, disable and delete actions answer 204, so there is no body
  // to read and the action itself says what happened.
  if (response == null) return { state: mapActionToResult(action), action };

  // A destination that reports itself disabled is a failed enable, not an enable.
  if (response.enabled === false) {
    return { state: SegmentAuthResult.Errored, action, enabled: false };
  }

  return { state: mapActionToResult(action), action, enabled: response.enabled };
}

export type DeleteSegmentInConnectionInput = {
  workspaceId: number;
};

/**
 * Clears the stored connection without touching the reader's Segment workspace.
 *
 * This is the escape hatch for a connection the OAuth actions cannot reach any
 * more — the destination was removed in Segment, or the authorization is gone — so
 * it takes no authorization code.
 *
 * Ember's service swallowed the failure and raised a snackbar, so its caller always
 * closed the modal. This rejects instead, which suits a mutation hook, but it means
 * a caller that only awaits it has to close its own modal on the failure path.
 */
export async function deleteSegmentInConnection({ workspaceId }: DeleteSegmentInConnectionInput): Promise<void> {
  await hydraUiApi.integrations.deleteSegmentIn({
    params: { path: { environment_id: String(workspaceId) } },
  });
}
